/**
 * AddRAFromOrgModal - FT-116: Add risk assessments from organisation roster
 *
 * Shows RAs NOT assigned to current vessel, allows assignment.
 * Uses useFleetRiskAssessments for org-scoped RA data.
 * Pattern mirrors FT-05 AddFromOrgModal.js for crew.
 */

import React, { useState, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { X, Search, AlertTriangle, Ship, Plus, FileText } from 'lucide-react';
import { useVessel } from '../../../contexts/VesselContext';
import { useFleetRiskAssessments } from '../../../hooks/domain/fleet/useFleetRiskAssessments';
import { RiskLevelBadge } from './RiskMatrix';

const AddRAFromOrgModal = ({ isOpen, onClose, onAssign }) => {
  const { currentVesselId, getCurrentVessel } = useVessel();
  const { list: allRAs, assignToVessel, getUnassignedToCurrentVessel } = useFleetRiskAssessments();
  const currentVessel = getCurrentVessel();

  const [searchTerm, setSearchTerm] = useState('');
  const [isAssigning, setIsAssigning] = useState(null); // Track which RA is being assigned

  // Get RAs NOT assigned to current vessel (excludes archived)
  const unassignedRAs = useMemo(() => {
    return getUnassignedToCurrentVessel();
  }, [getUnassignedToCurrentVessel]);

  // Filter by search
  const filteredRAs = useMemo(() => {
    if (!searchTerm.trim()) return unassignedRAs;
    const term = searchTerm.toLowerCase();
    return unassignedRAs.filter(ra =>
      (ra.title || '').toLowerCase().includes(term) ||
      (ra.category || '').toLowerCase().includes(term) ||
      (ra.location || '').toLowerCase().includes(term)
    );
  }, [unassignedRAs, searchTerm]);

  // Handle assign RA to current vessel
  const handleAssign = async (ra) => {
    setIsAssigning(ra.id);
    try {
      const result = assignToVessel(ra.id, currentVesselId);
      if (result && onAssign) {
        onAssign(ra);
      }
    } finally {
      setIsAssigning(null);
    }
  };

  if (!isOpen) return null;

  const modalRoot = document.getElementById('modal-root');

  // Get risk level color for badge
  const getRiskBgColor = (riskLevel) => {
    switch (riskLevel) {
      case 'extreme': return 'bg-red-100 text-red-800';
      case 'high': return 'bg-orange-100 text-orange-800';
      case 'medium': return 'bg-yellow-100 text-yellow-800';
      case 'low': return 'bg-green-100 text-green-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const modalContent = (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      {/* Backdrop */}
      <div className="fixed inset-0 bg-black bg-opacity-50" onClick={onClose} />

      {/* Modal */}
      <div className="relative min-h-screen flex items-center justify-center p-4">
        <div className="relative bg-white rounded-lg shadow-xl max-w-lg w-full max-h-[80vh] flex flex-col">
          {/* Header */}
          <div className="sticky top-0 bg-slate-700 rounded-t-lg px-6 py-4 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <AlertTriangle className="h-5 w-5 text-yellow-400" />
              <div>
                <h2 className="text-lg font-semibold text-white">
                  Add from Organisation
                </h2>
                <p className="text-sm text-slate-300">
                  Assign risk assessments to {currentVessel?.name || 'this vessel'}
                </p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="text-slate-300 hover:text-white"
            >
              <X className="h-6 w-6" />
            </button>
          </div>

          {/* Search */}
          <div className="p-4 border-b border-gray-200">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="text"
                placeholder="Search by title, category, or location..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md text-sm focus:ring-cyan-500 focus:border-cyan-500"
              />
            </div>
          </div>

          {/* RA List */}
          <div className="flex-1 overflow-y-auto p-4">
            {filteredRAs.length === 0 ? (
              <div className="text-center py-8">
                <FileText className="h-12 w-12 mx-auto text-gray-300 mb-3" />
                <p className="text-gray-500 font-medium">
                  {unassignedRAs.length === 0
                    ? 'All organisation risk assessments are assigned to this vessel'
                    : 'No risk assessments match your search'}
                </p>
                <p className="text-sm text-gray-400 mt-1">
                  {unassignedRAs.length === 0
                    ? 'Create new organisation-wide RAs to share across vessels'
                    : 'Try a different search term'}
                </p>
              </div>
            ) : (
              <div className="space-y-2">
                {filteredRAs.map((ra) => (
                  <div
                    key={ra.id}
                    className="flex items-center justify-between p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
                  >
                    <div className="flex items-center gap-3 flex-1 min-w-0">
                      <div className="w-10 h-10 rounded-full bg-yellow-100 flex items-center justify-center flex-shrink-0">
                        <AlertTriangle className="h-5 w-5 text-yellow-600" />
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="font-medium text-gray-900 truncate">{ra.title || 'Untitled'}</p>
                        <div className="flex items-center gap-2 mt-0.5">
                          <span className="text-xs text-gray-500">{ra.category || 'No category'}</span>
                          {ra.riskLevel && (
                            <span className={`px-1.5 py-0.5 rounded text-xs font-medium ${getRiskBgColor(ra.riskLevel)}`}>
                              {ra.riskLevel.charAt(0).toUpperCase() + ra.riskLevel.slice(1)}
                            </span>
                          )}
                          {ra.hazards?.length > 0 && (
                            <span className="text-xs text-gray-400">
                              {ra.hazards.length} hazard{ra.hazards.length !== 1 ? 's' : ''}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                    <button
                      onClick={() => handleAssign(ra)}
                      disabled={isAssigning === ra.id}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-white bg-cyan-600 rounded-md hover:bg-cyan-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex-shrink-0 ml-2"
                    >
                      {isAssigning === ra.id ? (
                        <>
                          <div className="h-4 w-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                          Assigning...
                        </>
                      ) : (
                        <>
                          <Plus className="h-4 w-4" />
                          Assign
                        </>
                      )}
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="border-t border-gray-200 px-6 py-4 bg-gray-50 rounded-b-lg flex justify-between items-center">
            <p className="text-sm text-gray-500">
              <Ship className="h-4 w-4 inline mr-1" />
              {unassignedRAs.length} risk assessment{unassignedRAs.length !== 1 ? 's' : ''} available to assign
            </p>
            <button
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );

  // Use portal if available, otherwise render inline
  return modalRoot ? createPortal(modalContent, modalRoot) : modalContent;
};

export default AddRAFromOrgModal;
